/**
 * Tenant create form (refreshed).
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectNative } from "@/components/ui/select-native";
import { toast } from "@/components/ui/sonner";
import { asApiError, postTenant, type TenantEnvironment } from "@/lib/api";
import { PageHeader } from "@/components/shell/PageHeader";

export function TenantNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState<TenantEnvironment>("live");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => postTenant({ name: name.trim(), environment }),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ["tenants"] });
      toast.success(`Tenant "${created.name}" created`);
      navigate(`/tenants/${created.id}`, { replace: true });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    mutation.mutate(undefined, {
      onError: (err) => {
        const body = asApiError(err);
        if (body?.error === "duplicate_name") {
          setError("A tenant with that name already exists.");
        } else if (body) {
          setError(body.detail ?? `Couldn't create tenant (${body.error}).`);
        } else {
          setError("Couldn't create tenant. Check your network and try again.");
        }
      },
    });
  }

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 h-auto px-2 text-muted-foreground">
        <Link to="/tenants">
          <ArrowLeft /> Back to tenants
        </Link>
      </Button>

      <PageHeader
        icon={Building2}
        title="New tenant"
        description="Create a new customer organization."
      />

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
          <CardDescription>
            Name must be unique within an environment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Holdings Ltd"
                required
                disabled={mutation.isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="env">Environment</Label>
              <SelectNative
                id="env"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as TenantEnvironment)}
                disabled={mutation.isPending}
              >
                <option value="live">Live</option>
                <option value="test">Test</option>
              </SelectNative>
              <p className="text-xs text-muted-foreground">
                Pick "Test" for staging. Cannot be changed after creation.
              </p>
            </div>
            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                <Plus />
                {mutation.isPending ? "Creating…" : "Create tenant"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}

export default TenantNewPage;
